import { useState } from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import type { Position } from '../../types';

interface ConnectionLineProps {
  from: Position;
  to: Position;
  connectionType: 'trigger-action' | 'data-flow' | 'webhook';
  onDelete?: () => void;
  readonly?: boolean;
  isTemporary?: boolean;
}

export function ConnectionLine({
  from,
  to,
  connectionType,
  onDelete,
  readonly = false,
  isTemporary = false
}: ConnectionLineProps) {
  const [isHovered, setIsHovered] = useState(false);

  // Calculate the path for the connection line
  const calculatePath = () => {
    const deltaX = to.x - from.x;
    const deltaY = to.y - from.y;
    
    // Control points for a smooth curve
    const controlPoint1X = from.x + deltaX * 0.5;
    const controlPoint1Y = from.y;
    const controlPoint2X = to.x - deltaX * 0.5;
    const controlPoint2Y = to.y;
    
    return `M ${from.x} ${from.y} C ${controlPoint1X} ${controlPoint1Y}, ${controlPoint2X} ${controlPoint2Y}, ${to.x} ${to.y}`;
  };

  // Calculate midpoint for the connection label
  const midPoint = {
    x: (from.x + to.x) / 2,
    y: (from.y + to.y) / 2
  };

  // Connection type styling
  const getConnectionStyle = () => {
    switch (connectionType) {
      case 'trigger-action':
        return {
          stroke: '#3b82f6', // blue
          strokeWidth: isTemporary ? 2 : 3,
          strokeDasharray: isTemporary ? '5,5' : 'none',
        };
      case 'data-flow':
        return {
          stroke: '#10b981', // green
          strokeWidth: 2,
          strokeDasharray: 'none',
        };
      case 'webhook':
        return {
          stroke: '#f59e0b', // amber
          strokeWidth: 2,
          strokeDasharray: '10,5',
        };
      default:
        return {
          stroke: '#6b7280', // gray
          strokeWidth: 2,
          strokeDasharray: 'none',
        };
    }
  };

  const connectionStyle = getConnectionStyle();

  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: 10 }}
    >
      {/* Connection Line */}
      <path
        d={calculatePath()}
        fill="none"
        {...connectionStyle}
        className={clsx(
          'transition-all duration-200',
          {
            'drop-shadow-lg': isHovered && !isTemporary,
            'opacity-70': isTemporary,
          }
        )}
        markerEnd={isTemporary ? undefined : 'url(#arrowhead)'}
      />

      {/* Arrow marker */}
      {!isTemporary && (
        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon
              points="0 0, 10 3.5, 0 7"
              fill={connectionStyle.stroke}
            />
          </marker>
        </defs>
      )}

      {/* Connection Label and Delete Button */}
      {!isTemporary && (
        <g
          className="pointer-events-auto"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Invisible larger path for easier hovering */}
          <path
            d={calculatePath()}
            fill="none"
            stroke="transparent"
            strokeWidth="20"
            className="cursor-pointer"
          />

          {/* Connection Type Label */}
          <g transform={`translate(${midPoint.x}, ${midPoint.y})`}>
            <rect
              x="-30"
              y="-12"
              width="60"
              height="24"
              rx="12"
              fill={isHovered ? connectionStyle.stroke : '#ffffff'}
              stroke={connectionStyle.stroke}
              strokeWidth="2"
              className={clsx(
                'transition-all duration-200',
                {
                  'drop-shadow-md': isHovered,
                }
              )}
            />
            <text
              textAnchor="middle"
              dy="4"
              className={clsx(
                'text-xs font-medium transition-colors duration-200',
                {
                  'fill-white': isHovered,
                  'fill-gray-700': !isHovered,
                }
              )}
            >
              {connectionType === 'trigger-action' ? 'Flow' : connectionType}
            </text>
          </g>

          {/* Delete Button */}
          {!readonly && onDelete && isHovered && (
            <g transform={`translate(${midPoint.x + 40}, ${midPoint.y})`}>
              <circle
                r="12"
                fill="#ef4444"
                className="cursor-pointer transition-colors duration-200 hover:fill-red-600"
                onClick={onDelete}
              />
              <TrashIcon
                x="-6"
                y="-6"
                width="12"
                height="12"
                className="fill-white pointer-events-none"
              />
            </g>
          )}
        </g>
      )}
    </svg>
  );
}